/**
 * Building construction.
 *
 * Every structure used to be one box with flat window quads pasted on
 * (VISUAL_AUDIT V-03, V-10, V-11). This assembles them from parts driven by the
 * per-building `style` data in `src/sim/data/world.ts`: roof form, storeys,
 * trim, porches, garages, fences, driveways, chimneys, shopfront glazing and
 * signage. Twelve houses become twelve houses rather than one house twelve
 * times, and the cost is the same builder run with different data.
 */
import * as THREE from 'three';
import type { WorldLocation } from '../../sim/data/world.js';
import {
  standard, windowMaterial, pavementMaterial, signTexture, wallMaterial,
  roofMaterial, lawnMaterial, shopGlassMaterial, shopInteriorMaterial,
  darkWindowMaterial,
} from './Materials.js';

export interface BuiltBuilding {
  group: THREE.Group;
  /** Footprints the player collides with, in world space, with heights. */
  colliders: { minX: number; maxX: number; minZ: number; maxZ: number; top: number }[];
  /** Shown when this location has work waiting. */
  marker: THREE.Group;
  /** The lawn mesh, so mowing can visibly change it. */
  lawn: THREE.Mesh | null;
}

const STOREY_HEIGHT = 3.2;

export function buildBuilding(location: WorldLocation): BuiltBuilding {
  const style = location.style ?? {};
  const group = new THREE.Group();
  group.position.set(location.x, 0, location.z);

  const colliders: { minX: number; maxX: number; minZ: number; maxZ: number; top: number }[] = [];
  const bodyColour = location.colour ?? 0xb0a89c;
  const trimColour = style.trimColour ?? shade(bodyColour, 0.28);
  const roofColour = style.roofColour ?? shade(bodyColour, -0.32);
  // `location.height` is the canonical wall height shared with the simulation
  // and the rest of the world data; `storeys` only decides how many rows of
  // windows go on it. Deriving the wall height from storeys instead made a
  // single-storey shop 3.2 m tall when the data said 8 m.
  const wallHeight = Math.max(2.6, location.height);
  const storeys = Math.max(
    1,
    Math.min(style.storeys ?? Math.round(wallHeight / STOREY_HEIGHT),
             Math.floor(wallHeight / 2.6)),
  );
  const storeyHeight = wallHeight / storeys;

  // Textured surfaces rather than flat colours: siding lines on walls and
  // shingle rows on roofs are what stop a building reading as an extruded
  // rectangle from across the street.
  const bodyMat = wallMaterial(bodyColour, location.kind === 'customer' || location.kind === 'home');
  const trimMat = standard(trimColour, { rough: 0.75 });
  const roofMat = roofMaterial(roofColour);

  // Which face the entrance is on, in local space.
  const sideEntry = Math.abs(location.entryX) > Math.abs(location.entryZ);
  const entryNormal = new THREE.Vector3(
    sideEntry ? Math.sign(location.entryX) : 0,
    0,
    sideEntry ? 0 : Math.sign(location.entryZ),
  );

  // --- yard ----------------------------------------------------------------
  let lawn: THREE.Mesh | null = null;
  if (location.kind === 'customer' || location.kind === 'home') {
    const upkeep = style.upkeep ?? 0.6;
    // Well-kept lawns are a richer green; neglected ones go yellow and patchy.
    // This is the clearest possible read on which houses need your services.
    lawn = new THREE.Mesh(
      new THREE.PlaneGeometry(location.width + 11, location.depth + 11, 1, 1),
      lawnMaterial(upkeep),
    );
    lawn.rotation.x = -Math.PI / 2;
    lawn.position.y = 0.055;
    lawn.receiveShadow = true;
    lawn.userData.upkeep = upkeep;
    group.add(lawn);
  }

  // --- foundation ----------------------------------------------------------
  const plinth = new THREE.Mesh(
    new THREE.BoxGeometry(location.width + 0.7, 0.55, location.depth + 0.7),
    standard(shade(bodyColour, -0.42), { rough: 1 }),
  );
  plinth.position.y = 0.27;
  plinth.receiveShadow = true;
  plinth.castShadow = true;
  group.add(plinth);

  // A weathering course stepping the plinth back to the wall
  // (VISUAL_REALISM_AUDIT R-06: nothing in the scene had an edge that was not
  // a perfect right angle). Two shallow steps rather than one hard shoulder
  // give the new ambient occlusion a groove to darken, which is what reads as
  // a real masonry base at gameplay distance.
  for (const [inset, y, h] of [[0.46, 0.575, 0.09], [0.24, 0.5, 0.11]] as const) {
    const course = new THREE.Mesh(
      new THREE.BoxGeometry(location.width + inset, h, location.depth + inset),
      standard(shade(bodyColour, -0.3), { rough: 0.95 }),
    );
    course.position.y = y;
    course.castShadow = true;
    course.receiveShadow = true;
    group.add(course);
  }

  // --- walls ---------------------------------------------------------------
  const body = new THREE.Mesh(
    new THREE.BoxGeometry(location.width, wallHeight, location.depth),
    bodyMat,
  );
  body.position.y = 0.55 + wallHeight / 2;
  body.castShadow = true;
  body.receiveShadow = true;
  group.add(body);

  // Storey bands break the wall vertically so a three-storey block does not
  // read as one extruded rectangle.
  for (let i = 1; i < storeys; i++) {
    const band = new THREE.Mesh(
      new THREE.BoxGeometry(location.width + 0.25, 0.22, location.depth + 0.25),
      trimMat,
    );
    band.position.y = 0.55 + i * storeyHeight;
    band.castShadow = true;
    group.add(band);
  }

  // Corner posts.
  for (const sx of [-1, 1]) {
    for (const sz of [-1, 1]) {
      const post = new THREE.Mesh(
        new THREE.BoxGeometry(0.34, wallHeight, 0.34),
        trimMat,
      );
      post.position.set(
        sx * (location.width / 2 - 0.05),
        0.55 + wallHeight / 2,
        sz * (location.depth / 2 - 0.05),
      );
      post.castShadow = true;
      group.add(post);
    }
  }

  // --- windows -------------------------------------------------------------
  const winMat = windowMaterial();
  addWindows(group, location, storeys, storeyHeight, winMat, trimMat, entryNormal, !!style.shopfront);

  // --- roof ----------------------------------------------------------------
  const roofTop = 0.55 + wallHeight;
  addRoof(group, location, style.roof ?? 'gable', roofTop, roofMat, trimMat);

  if (style.chimney) {
    const chimney = new THREE.Mesh(
      new THREE.BoxGeometry(1.05, 2.6, 1.05),
      standard(shade(roofColour, -0.12), { rough: 1 }),
    );
    chimney.position.set(location.width * 0.26, roofTop + 2.1, -location.depth * 0.2);
    chimney.castShadow = true;
    group.add(chimney);
    const cap = new THREE.Mesh(
      new THREE.BoxGeometry(1.3, 0.22, 1.3),
      standard(shade(roofColour, -0.3), { rough: 1 }),
    );
    cap.position.set(chimney.position.x, roofTop + 3.5, chimney.position.z);
    group.add(cap);
  }

  // --- entrance ------------------------------------------------------------
  addEntrance(group, location, entryNormal, sideEntry, trimMat, bodyColour, style);

  // --- porch / awning ------------------------------------------------------
  if (style.porch) {
    addPorch(group, location, entryNormal, sideEntry, trimMat, roofMat);
  }

  // --- signage -------------------------------------------------------------
  if (style.sign) {
      addSign(
      group, location, entryNormal, sideEntry, style.sign,
      style.signColour ?? 0xf2e6d4, bodyColour, wallHeight,
    );
  }

  // --- garage --------------------------------------------------------------
  if (style.garage) {
    const garageWidth = 6.4;
    const garageDepth = Math.min(location.depth * 0.72, 8.5);
    const garageHeight = 3.4;
    const side = location.width / 2 + garageWidth / 2 - 0.4;
    const garage = new THREE.Mesh(
      new THREE.BoxGeometry(garageWidth, garageHeight, garageDepth),
      bodyMat,
    );
    garage.position.set(side, 0.55 + garageHeight / 2, location.depth * 0.1);
    garage.castShadow = true;
    garage.receiveShadow = true;
    group.add(garage);

    const garageRoof = new THREE.Mesh(
      new THREE.BoxGeometry(garageWidth + 0.7, 0.32, garageDepth + 0.7),
      roofMat,
    );
    garageRoof.position.set(side, 0.55 + garageHeight + 0.16, garage.position.z);
    garageRoof.castShadow = true;
    group.add(garageRoof);

    const door = new THREE.Mesh(
      new THREE.BoxGeometry(garageWidth - 1.1, 2.5, 0.2),
      trimMat,
    );
    const facing = Math.sign(location.entryZ) || 1;
    door.position.set(side, 0.55 + 1.25, garage.position.z + facing * (garageDepth / 2));
    group.add(door);

    colliders.push({
      minX: location.x + side - garageWidth / 2,
      maxX: location.x + side + garageWidth / 2,
      minZ: location.z + garage.position.z - garageDepth / 2,
      maxZ: location.z + garage.position.z + garageDepth / 2,
      top: 0.55 + garageHeight + 0.5,
    });
  }

  // --- driveway ------------------------------------------------------------
  if (style.driveway) {
    const facing = sideEntry ? Math.sign(location.entryX) : Math.sign(location.entryZ);
    const drive = new THREE.Mesh(
      new THREE.PlaneGeometry(
        sideEntry ? 16 : 5.6,
        sideEntry ? 5.6 : 16,
      ),
      pavementMaterial(),
    );
    drive.rotation.x = -Math.PI / 2;
    drive.position.set(
      sideEntry ? facing * (location.width / 2 + 8) : (style.garage ? location.width / 2 + 2.6 : 0),
      0.07,
      sideEntry ? 0 : facing * (location.depth / 2 + 8),
    );
    drive.receiveShadow = true;
    group.add(drive);
  }

  // --- fence ---------------------------------------------------------------
  if (style.fence) {
    addFence(group, location, style.fenceColour ?? 0xe8e2d6, entryNormal, sideEntry);
  }

  // --- yard dressing -------------------------------------------------------
  if (location.kind === 'customer' || location.kind === 'home') {
    addYardDressing(group, location, entryNormal, sideEntry, style.upkeep ?? 0.6);
  } else if (location.kind === 'depot') {
    addDepotYard(group, location, sideEntry);
  } else if (location.kind !== 'park') {
    addCommercialFrontage(group, location, sideEntry);
  }

  // --- job marker ----------------------------------------------------------
  const marker = buildMarker(roofTop + 5.5);
  group.add(marker);

  colliders.push({
    minX: location.x - location.width / 2,
    maxX: location.x + location.width / 2,
    minZ: location.z - location.depth / 2,
    maxZ: location.z + location.depth / 2,
    // Roof ridge, so a camera lifted above the roofline is not treated as
    // blocked by the building beneath it.
    top: 0.55 + wallHeight + 4.5,
  });

  return { group, colliders, marker, lawn };
}

function addWindows(
  group: THREE.Group,
  location: WorldLocation,
  storeys: number,
  storeyHeight: number,
  winMat: THREE.MeshStandardMaterial,
  trimMat: THREE.MeshStandardMaterial,
  entryNormal: THREE.Vector3,
  shopfront: boolean,
): void {
  const faces: { normal: THREE.Vector3; width: number }[] = [
    { normal: new THREE.Vector3(0, 0, 1), width: location.width },
    { normal: new THREE.Vector3(0, 0, -1), width: location.width },
    { normal: new THREE.Vector3(1, 0, 0), width: location.depth },
    { normal: new THREE.Vector3(-1, 0, 0), width: location.depth },
  ];

  for (const face of faces) {
    const isEntryFace = face.normal.dot(entryNormal) > 0.5;
    const half = face.normal.x !== 0 ? location.width / 2 : location.depth / 2;
    const offset = half + 0.09;

    // A shopfront replaces the ground floor windows with full glazing.
    if (shopfront && isEntryFace) {
      const glassHeight = Math.min(storeyHeight * 0.72, 3.2);
      const glassY = 0.55 + glassHeight / 2 + 0.35;
      const glass = new THREE.Mesh(
        new THREE.PlaneGeometry(face.width - 2.4, glassHeight),
        shopGlassMaterial(),
      );
      glass.renderOrder = 3;
      place(glass, face.normal, offset, glassY);
      group.add(glass);

      // Something behind the glass. A shopfront that is a flat blue band reads
      // as a painted wall; a lit recess with shelving in it reads as a shop
      // you have simply not been let into yet - which is exactly what it is.
      addShopInterior(group, face.normal, half, face.width - 2.4, glassHeight, glassY, place);

      for (let i = -1; i <= 1; i++) {
        const mullion = new THREE.Mesh(
          new THREE.BoxGeometry(0.16, glassHeight + 0.2, 0.16), trimMat,
        );
        place(mullion, face.normal, offset, glassY, i * (face.width - 2.4) / 3);
        group.add(mullion);
      }
      // Upper storeys still get normal windows.
      addRows(group, face, offset, 1, storeys, storeyHeight, winMat, trimMat, location);
      continue;
    }

    addRows(group, face, offset, 0, storeys, storeyHeight, winMat, trimMat, location);
  }

  function place(
    mesh: THREE.Object3D,
    normal: THREE.Vector3,
    dist: number,
    y: number,
    along = 0,
  ): void {
    if (normal.z !== 0) {
      mesh.position.set(along, y, normal.z * dist);
      mesh.rotation.y = normal.z > 0 ? 0 : Math.PI;
    } else {
      mesh.position.set(normal.x * dist, y, along);
      mesh.rotation.y = normal.x > 0 ? Math.PI / 2 : -Math.PI / 2;
    }
  }

  function addRows(
    target: THREE.Group,
    face: { normal: THREE.Vector3; width: number },
    offset: number,
    fromStorey: number,
    totalStoreys: number,
    perStorey: number,
    glassMat: THREE.MeshStandardMaterial,
    frameMat: THREE.MeshStandardMaterial,
    loc: WorldLocation,
  ): void {
    const columns = Math.max(1, Math.floor(face.width / 4.4));
    const spacing = face.width / columns;
    for (let storey = fromStorey; storey < totalStoreys; storey++) {
      const y = 0.55 + storey * perStorey + perStorey * 0.55;
      for (let c = 0; c < columns; c++) {
        const along = (c - (columns - 1) / 2) * spacing;
        // Skip the window directly over the entrance on the ground floor.
        const isEntryFace = face.normal.dot(entryNormal) > 0.5;
        if (storey === 0 && isEntryFace && Math.abs(along) < 1.9) continue;

        // A reveal: the glass sits *inside* the wall, not on it
        // (VISUAL_REALISM_AUDIT R-08). The returns around the opening are what
        // give a window a shadowed edge and tell the eye the wall has
        // thickness. A pane flush with the facade reads as a sticker, and the
        // new ambient occlusion has nothing to bite on.
        //
        // The frame has to be a ring of four bars, not a solid panel. The
        // first attempt kept the old solid 1.85 x 1.95 box and simply pushed
        // the glass behind it, which turned every window on the street into a
        // blank cream rectangle - the box's own back face.
        //
        // The reveal has to be built *outward*. The walls are solid boxes with
        // no openings cut in them, so a pane set behind the facade is simply
        // inside the wall and invisible - the second attempt did that and every
        // window showed plain siding. A frame that stands proud of the facade
        // puts the pane at the bottom of a real recess, casts real shadow into
        // it, and costs four boxes instead of a CSG subtraction.
        const reveal = 0.17;
        const clearW = 1.62;
        const clearH = 1.7;
        const barW = (1.9 - clearW) / 2;
        const barH = (2.0 - clearH) / 2;
        for (const [w, h, ox, oy] of [
          [1.9, barH, 0, (clearH + barH) / 2],
          [1.9, barH, 0, -(clearH + barH) / 2],
          [barW, clearH, (clearW + barW) / 2, 0],
          [barW, clearH, -(clearW + barW) / 2, 0],
        ] as [number, number, number, number][]) {
          const bar = new THREE.Mesh(new THREE.BoxGeometry(w, h, reveal), frameMat);
          place(bar, face.normal, offset + reveal / 2, y + oy, along + ox);
          target.add(bar);
        }

        // About a third of rooms are empty, deterministically per address and
        // per pane. A street where every window lights up at the same moment
        // reads as a lighting effect, not as people being home.
        const occupied = paneHash(loc.id, face.normal, storey, c) % 3 !== 0;
        const glass = new THREE.Mesh(
          new THREE.PlaneGeometry(1.5, 1.6),
          occupied ? glassMat : darkWindowMaterial(),
        );
        // At the bottom of the recess the frame makes, not proud of it.
        place(glass, face.normal, offset + 0.015, y, along);
        target.add(glass);

        // A sill gives the wall a shadow line, which is most of what makes a
        // facade look built rather than printed.
        // The sill oversails the frame, so it throws a shadow line down the
        // wall rather than sitting flush in it.
        const sill = new THREE.Mesh(new THREE.BoxGeometry(2.2, 0.14, 0.34), frameMat);
        place(sill, face.normal, offset + 0.13, y - 1.07, along);
        target.add(sill);
      }
    }
  }
}

/** Stable per-pane hash, so the same rooms are dark every session. */
function paneHash(
  id: string, normal: THREE.Vector3, storey: number, column: number,
): number {
  let h = (storey + 1) * 7919 + (column + 1) * 104729;
  h += (normal.x + 2) * 31 + (normal.z + 2) * 131;
  for (const ch of id) h = (h * 31 + ch.charCodeAt(0)) & 0x7fffffff;
  return h >>> 0;
}

/**
 * The lit interior seen through a shopfront: one painted panel behind the glass.
 *
 * See `shopInteriorMaterial` for why this is a texture rather than a modelled
 * recess - the building is a solid box, and anything placed behind its facade
 * is inside the wall.
 */
function addShopInterior(
  group: THREE.Group,
  normal: THREE.Vector3,
  half: number,
  width: number,
  height: number,
  centreY: number,
  place: (mesh: THREE.Object3D, normal: THREE.Vector3, dist: number, y: number, along?: number) => void,
): void {
  const panel = new THREE.Mesh(
    new THREE.PlaneGeometry(width, height),
    shopInteriorMaterial(),
  );
  // A centimetre inside the glazing plane, which sits at half + 0.09.
  place(panel, normal, half + 0.08, centreY);
  group.add(panel);
}

function addRoof(
  group: THREE.Group,
  location: WorldLocation,
  kind: 'gable' | 'hip' | 'flat' | 'shed',
  top: number,
  roofMat: THREE.MeshStandardMaterial,
  trimMat: THREE.MeshStandardMaterial,
): void {
  const overhang = 0.85;
  const w = location.width + overhang * 2;
  const d = location.depth + overhang * 2;

  // Eaves board on every roof type: the dark line under the roof edge is what
  // separates roof from wall at a glance.
  const eaves = new THREE.Mesh(new THREE.BoxGeometry(w, 0.34, d), trimMat);
  eaves.position.y = top + 0.17;
  eaves.castShadow = true;
  group.add(eaves);

  // A cornice stepping out from wall to eaves. Same reasoning as the plinth
  // course: a moulding is a stack of small offsets, and the shadow each one
  // throws is most of what the eye uses to judge that a wall is built rather
  // than extruded.
  for (const [inset, y, h] of [[0.28, top - 0.05, 0.1], [0.56, top + 0.03, 0.08]] as const) {
    const band = new THREE.Mesh(
      new THREE.BoxGeometry(location.width + inset, h, location.depth + inset),
      trimMat,
    );
    band.position.y = y;
    band.castShadow = true;
    group.add(band);
  }

  if (kind === 'flat') {
    const slab = new THREE.Mesh(new THREE.BoxGeometry(w, 0.4, d), roofMat);
    slab.position.y = top + 0.5;
    slab.castShadow = true;
    group.add(slab);
    // Parapet wall around the edge.
    for (const [sx, sz, bw, bd] of [
      [0, d / 2, w, 0.35], [0, -d / 2, w, 0.35],
      [w / 2, 0, 0.35, d], [-w / 2, 0, 0.35, d],
    ] as [number, number, number, number][]) {
      const wall = new THREE.Mesh(new THREE.BoxGeometry(bw, 0.9, bd), trimMat);
      wall.position.set(sx, top + 1.15, sz);
      wall.castShadow = true;
      group.add(wall);
    }
    return;
  }

  if (kind === 'shed') {
    // Single slope. The slab is shortened so that, once tilted, its footprint
    // still matches the building — an untilted-length slab overhangs badly.
    const rise = Math.min(1.9, d * 0.16);
    const pitch = Math.atan2(rise, d);
    const slab = new THREE.Mesh(new THREE.BoxGeometry(w, 0.3, d / Math.cos(pitch)), roofMat);
    slab.position.y = top + 0.34 + rise / 2;
    slab.rotation.x = -pitch;
    slab.castShadow = true;
    group.add(slab);
    // Close the triangular ends so the slope does not read as a floating plank.
    for (const sx of [-1, 1]) {
      const shape = new THREE.Shape();
      shape.moveTo(-d / 2, 0);
      shape.lineTo(d / 2, 0);
      shape.lineTo(d / 2, rise);
      shape.closePath();
      const end = new THREE.Mesh(new THREE.ShapeGeometry(shape), roofMat);
      end.position.set(sx * (w / 2 - 0.02), top + 0.2, 0);
      end.rotation.y = sx * Math.PI / 2;
      group.add(end);
    }
    return;
  }

  // Gable and hip are both pyramids; a gable is stretched along one axis.
  const rise = kind === 'hip' ? Math.min(3.4, Math.min(w, d) * 0.34) : Math.min(3.8, d * 0.42);
  const radius = Math.sqrt((w / 2) ** 2 + (d / 2) ** 2);
  const roof = new THREE.Mesh(new THREE.ConeGeometry(radius, rise, 4, 1), roofMat);
  roof.rotation.y = Math.PI / 4;
  roof.position.y = top + 0.34 + rise / 2;
  // Squash the pyramid onto the building's actual footprint.
  roof.scale.set(w / (radius * Math.SQRT2), 1, d / (radius * Math.SQRT2));
  if (kind === 'gable') roof.scale.z *= 1.0;
  roof.castShadow = true;
  group.add(roof);

  // Ridge cap.
  const ridge = new THREE.Mesh(
    new THREE.BoxGeometry(kind === 'gable' ? w * 0.9 : 1.2, 0.24, 1.2),
    trimMat,
  );
  ridge.position.y = top + 0.34 + rise;
  group.add(ridge);
}

function addEntrance(
  group: THREE.Group,
  location: WorldLocation,
  entryNormal: THREE.Vector3,
  sideEntry: boolean,
  trimMat: THREE.MeshStandardMaterial,
  bodyColour: number,
  style: NonNullable<WorldLocation['style']>,
): void {
  const holder = new THREE.Group();
  const doorColour = style.roofColour ?? shade(bodyColour, -0.42);

  const frame = new THREE.Mesh(new THREE.BoxGeometry(3, 3.3, 0.28), trimMat);
  frame.position.y = 1.65;
  holder.add(frame);

  const leaf = new THREE.Mesh(
    new THREE.BoxGeometry(2.2, 2.75, 0.36),
    standard(doorColour, { rough: 0.6 }),
  );
  leaf.position.y = 1.42;
  leaf.castShadow = true;
  holder.add(leaf);

  // Glazed panel and a handle: small, but the eye reads "door" instantly.
  const panel = new THREE.Mesh(new THREE.PlaneGeometry(1.3, 0.85), windowMaterial());
  panel.position.set(0, 2.2, 0.2);
  holder.add(panel);

  const handle = new THREE.Mesh(
    new THREE.SphereGeometry(0.09, 8, 6),
    standard(0xc8b070, { rough: 0.3, metal: 0.75 }),
  );
  handle.position.set(0.75, 1.35, 0.22);
  holder.add(handle);

  // Two steps up to the threshold.
  for (let i = 0; i < 2; i++) {
    const step = new THREE.Mesh(
      new THREE.BoxGeometry(3.4 - i * 0.4, 0.2, 0.7 + i * 0.35),
      standard(shade(bodyColour, -0.34), { rough: 1 }),
    );
    step.position.set(0, 0.1 + i * 0.2, 0.75 + i * 0.2);
    step.receiveShadow = true;
    holder.add(step);

    // A nosing: the tread oversails its riser by a couple of centimetres, so
    // the step edge carries a shadow line instead of dying into the one below.
    const nosing = new THREE.Mesh(
      new THREE.BoxGeometry(3.46 - i * 0.4, 0.055, 0.1),
      standard(shade(bodyColour, -0.5), { rough: 1 }),
    );
    nosing.position.set(0, 0.175 + i * 0.2, 1.1 + i * 0.375);
    nosing.castShadow = true;
    holder.add(nosing);
  }

  const offset = (sideEntry ? location.width : location.depth) / 2 + 0.14;
  if (sideEntry) {
    holder.position.set(entryNormal.x * offset, 0, 0);
    holder.rotation.y = entryNormal.x > 0 ? Math.PI / 2 : -Math.PI / 2;
  } else {
    holder.position.set(0, 0, entryNormal.z * offset);
    if (entryNormal.z < 0) holder.rotation.y = Math.PI;
  }
  group.add(holder);
}

function addPorch(
  group: THREE.Group,
  location: WorldLocation,
  entryNormal: THREE.Vector3,
  sideEntry: boolean,
  trimMat: THREE.MeshStandardMaterial,
  roofMat: THREE.MeshStandardMaterial,
): void {
  const holder = new THREE.Group();
  const span = Math.min((sideEntry ? location.depth : location.width) * 0.62, 9);
  const depth = 2.8;

  const canopy = new THREE.Mesh(new THREE.BoxGeometry(span, 0.28, depth), roofMat);
  canopy.position.set(0, 3.35, depth / 2 - 0.2);
  canopy.castShadow = true;
  holder.add(canopy);

  const fascia = new THREE.Mesh(new THREE.BoxGeometry(span + 0.2, 0.4, 0.24), trimMat);
  fascia.position.set(0, 3.25, depth - 0.3);
  holder.add(fascia);

  for (const sx of [-1, 1]) {
    const post = new THREE.Mesh(new THREE.BoxGeometry(0.24, 3.2, 0.24), trimMat);
    post.position.set(sx * (span / 2 - 0.3), 1.6, depth - 0.4);
    post.castShadow = true;
    holder.add(post);
  }

  const offset = (sideEntry ? location.width : location.depth) / 2;
  if (sideEntry) {
    holder.position.set(entryNormal.x * offset, 0, 0);
    holder.rotation.y = entryNormal.x > 0 ? Math.PI / 2 : -Math.PI / 2;
  } else {
    holder.position.set(0, 0, entryNormal.z * offset);
    if (entryNormal.z < 0) holder.rotation.y = Math.PI;
  }
  group.add(holder);
}

function addSign(
  group: THREE.Group,
  location: WorldLocation,
  entryNormal: THREE.Vector3,
  sideEntry: boolean,
  text: string,
  colour: number,
  bodyColour: number,
  wallHeight: number,
): void {
  const faceWidth = sideEntry ? location.depth : location.width;
  const boardWidth = Math.min(faceWidth - 1.6, 17);
  const board = new THREE.Group();

  const backing = new THREE.Mesh(
    new THREE.BoxGeometry(boardWidth, 1.5, 0.3),
    standard(shade(bodyColour, -0.4), { rough: 0.7 }),
  );
  board.add(backing);

  // The lettering is a generated canvas texture — no font files shipped.
  const label = new THREE.Mesh(
    new THREE.PlaneGeometry(boardWidth - 0.4, 1.15),
    new THREE.MeshBasicMaterial({
      map: signTexture(text, colour),
      transparent: true,
    }),
  );
  label.position.z = 0.17;
  board.add(label);

  const offset = (sideEntry ? location.width : location.depth) / 2 + 0.2;
  // Sits just under the eaves, on the wall, rather than floating above it.
  const y = 0.55 + wallHeight - 1.15;
  if (sideEntry) {
    board.position.set(entryNormal.x * offset, y, 0);
    board.rotation.y = entryNormal.x > 0 ? Math.PI / 2 : -Math.PI / 2;
  } else {
    board.position.set(0, y, entryNormal.z * offset);
    if (entryNormal.z < 0) board.rotation.y = Math.PI;
  }
  group.add(board);
}

function addFence(
  group: THREE.Group,
  location: WorldLocation,
  colour: number,
  entryNormal: THREE.Vector3,
  sideEntry: boolean,
): void {
  const mat = standard(colour, { rough: 0.85 });
  const halfW = location.width / 2 + 5;
  const halfD = location.depth / 2 + 5;
  const postGeo = new THREE.BoxGeometry(0.16, 1.05, 0.16);
  const railGeo = new THREE.BoxGeometry(1.5, 0.1, 0.08);

  const gateAxis = sideEntry ? 'x' : 'z';
  const gateSign = sideEntry ? Math.sign(entryNormal.x) : Math.sign(entryNormal.z);

  const run = (
    from: THREE.Vector2, to: THREE.Vector2, skipGate: boolean,
  ) => {
    const length = from.distanceTo(to);
    const count = Math.max(2, Math.round(length / 1.5));
    for (let i = 0; i <= count; i++) {
      const t = i / count;
      const x = from.x + (to.x - from.x) * t;
      const z = from.y + (to.y - from.y) * t;
      // Leave a gap where the path meets the street.
      if (skipGate && Math.abs(gateAxis === 'x' ? z : x) < 2.2) continue;
      const post = new THREE.Mesh(postGeo, mat);
      post.position.set(x, 0.52, z);
      post.castShadow = true;
      group.add(post);
      if (i < count) {
        for (const railY of [0.35, 0.8]) {
          const rail = new THREE.Mesh(railGeo, mat);
          rail.position.set(
            x + (to.x - from.x) / count / 2,
            railY,
            z + (to.y - from.y) / count / 2,
          );
          rail.rotation.y = Math.atan2(to.x - from.x, to.y - from.y) + Math.PI / 2;
          group.add(rail);
        }
      }
    }
  };

  const corners = [
    new THREE.Vector2(-halfW, -halfD),
    new THREE.Vector2(halfW, -halfD),
    new THREE.Vector2(halfW, halfD),
    new THREE.Vector2(-halfW, halfD),
  ];
  for (let i = 0; i < 4; i++) {
    const from = corners[i]!;
    const to = corners[(i + 1) % 4]!;
    const onGateSide =
      (gateAxis === 'z' && gateSign > 0 && from.y === halfD && to.y === halfD) ||
      (gateAxis === 'z' && gateSign < 0 && from.y === -halfD && to.y === -halfD) ||
      (gateAxis === 'x' && gateSign > 0 && from.x === halfW && to.x === halfW) ||
      (gateAxis === 'x' && gateSign < 0 && from.x === -halfW && to.x === -halfW);
    run(from, to, onGateSide);
  }
}

/**
 * Everything in a front yard that is not the house.
 *
 * A bare rectangle of grass in front of a box is what makes a procedural
 * neighbourhood read as a diagram. It is also the game's clearest piece of
 * storytelling: `upkeep` grades a property from "nobody has touched this in a
 * year" to "these people pay someone", so the player can read which houses need
 * their services, and which life they are working toward, without any UI.
 */
function addYardDressing(
  group: THREE.Group,
  location: WorldLocation,
  entryNormal: THREE.Vector3,
  sideEntry: boolean,
  upkeep: number,
): void {
  // Deterministic per property: the same house is dressed the same way in
  // every session, so the neighbourhood is a place rather than a shuffle.
  let seed = 0;
  for (const ch of location.id) seed = (seed * 31 + ch.charCodeAt(0)) & 0x7fffffff;
  const rand = () => {
    seed = (seed * 1103515245 + 12345) & 0x7fffffff;
    return seed / 0x7fffffff;
  };

  const outward = sideEntry ? Math.sign(location.entryX) : Math.sign(location.entryZ);
  const halfAcross = (sideEntry ? location.depth : location.width) / 2;
  const halfOut = (sideEntry ? location.width : location.depth) / 2;
  /** Local position from "distance out from the entrance face" and "sideways". */
  const at = (out: number, across: number): [number, number] =>
    sideEntry ? [outward * (halfOut + out), across] : [across, outward * (halfOut + out)];

  const pathMat = pavementMaterial();
  const soilMat = standard(shade(0x4a3a2a, upkeep * 0.2), { rough: 1 });
  // Neglected planting goes dull and yellow-green; kept planting is a deep,
  // saturated green. Both stay green: the first version darkened the low end
  // toward black, which read as scorched earth rather than as an unloved hedge.
  const hedgeMat = standard(
    shade(upkeep > 0.55 ? 0x3d6f31 : 0x76783a, Math.max(-0.1, (upkeep - 0.55) * 0.3)),
    { rough: 1 },
  );

  // --- front path ----------------------------------------------------------
  {
    const [px, pz] = at(4.6, 0);
    const path = new THREE.Mesh(
      new THREE.PlaneGeometry(sideEntry ? 9.6 : 1.9, sideEntry ? 1.9 : 9.6),
      pathMat,
    );
    path.rotation.x = -Math.PI / 2;
    path.position.set(px, 0.075, pz);
    path.receiveShadow = true;
    group.add(path);
  }

  // --- foundation planting along the entrance face -------------------------
  {
    const beds = 3 + Math.floor(rand() * 3);
    for (let i = 0; i < beds; i++) {
      // Skip the path itself, and leave gaps on a neglected property.
      const across = (rand() - 0.5) * 2 * (halfAcross - 1.2);
      if (Math.abs(across) < 1.8) continue;
      if (rand() > 0.35 + upkeep * 0.6) continue;
      const radius = (0.42 + rand() * 0.34) * (0.72 + upkeep * 0.5);
      const [hx, hz] = at(1.5 + rand() * 0.7, across);
      const lobes = 2 + Math.floor(rand() * 2);
      for (let j = 0; j < lobes; j++) {
        const lobe = new THREE.Mesh(
          new THREE.IcosahedronGeometry(radius * (j === 0 ? 1 : 0.7), 0),
          hedgeMat,
        );
        lobe.position.set(
          hx + (j === 0 ? 0 : (rand() - 0.5) * radius),
          radius * 0.72,
          hz + (j === 0 ? 0 : (rand() - 0.5) * radius),
        );
        lobe.scale.set(1, 0.8, 1);
        lobe.rotation.y = rand() * Math.PI;
        lobe.castShadow = true;
        lobe.receiveShadow = true;
        group.add(lobe);
      }
    }
  }

  // --- boundary hedge ------------------------------------------------------
  // Without a boundary, twelve properties read as one continuous green field
  // and the neighbourhood has no grain. The hedge is broken for the path, and
  // for the driveway where there is one; a neglected property's is patchier
  // and lower, which is a second reading of the same `upkeep` number.
  {
    // The lawn reaches 5.5 m past the building on every side; the hedge marks
    // that line from just inside it.
    const run = halfAcross + 4.2;
    const height = 0.42 + upkeep * 0.34;
    let along = -run;
    let guard = 0;
    while (along < run && guard++ < 80) {
      const step = height * 1.45;
      // Leave the front path clear, and the driveway side of a wide frontage.
      const onPath = Math.abs(along) < 1.9;
      const gap = rand() > 0.18 + upkeep * 0.75;
      if (onPath || gap) { along += step; continue; }
      const radius = height * (0.85 + rand() * 0.3);
      const [hx, hz] = at(4.9, along);
      const lobe = new THREE.Mesh(
        new THREE.IcosahedronGeometry(radius, 0),
        hedgeMat,
      );
      lobe.position.set(hx, radius * 0.62, hz);
      lobe.scale.set(1, 0.72, 1);
      lobe.rotation.y = rand() * Math.PI;
      lobe.castShadow = true;
      lobe.receiveShadow = true;
      group.add(lobe);
      along += step;
    }
  }

  // --- mailbox at the kerb -------------------------------------------------
  {
    const across = (halfAcross - 1.4) * (rand() > 0.5 ? 1 : -1);
    const [mx, mz] = at(6.6, across);
    const postMat = standard(0x6b5842, { rough: 1 });
    const post = new THREE.Mesh(new THREE.BoxGeometry(0.13, 1.15, 0.13), postMat);
    post.position.set(mx, 0.63, mz);
    post.castShadow = true;
    group.add(post);
    const box = new THREE.Mesh(
      new THREE.BoxGeometry(sideEntry ? 0.34 : 0.52, 0.3, sideEntry ? 0.52 : 0.34),
      standard(upkeep > 0.55 ? 0x2f3742 : 0x5a5147, { rough: 0.8, metal: 0.25 }),
    );
    box.position.set(mx, 1.32, mz);
    box.castShadow = true;
    group.add(box);
    // A leaning mailbox says more about a neglected property than a texture can.
    if (upkeep < 0.45) {
      post.rotation.z = 0.16;
      box.rotation.z = 0.16;
    }
  }

  // --- wheelie bin ---------------------------------------------------------
  {
    const across = -(halfAcross - 1.1) * (rand() > 0.5 ? 1 : -1);
    const [bx, bz] = at(2.2 + rand() * 1.4, across);
    const bin = new THREE.Mesh(
      new THREE.BoxGeometry(0.62, 1.06, 0.56),
      standard(rand() > 0.5 ? 0x2c5c3a : 0x2f3a4a, { rough: 0.85 }),
    );
    bin.position.set(bx, 0.53, bz);
    bin.rotation.y = (rand() - 0.5) * 0.8;
    bin.castShadow = true;
    group.add(bin);
    const lid = new THREE.Mesh(
      new THREE.BoxGeometry(0.68, 0.1, 0.62),
      standard(0x1f2833, { rough: 0.85 }),
    );
    lid.position.set(bx, 1.1, bz);
    lid.rotation.y = bin.rotation.y;
    group.add(lid);
  }

  // --- the reward tier: kept properties get a flower bed -------------------
  if (upkeep > 0.7) {
    const across = (halfAcross - 2.6) * (rand() > 0.5 ? 1 : -1);
    const [fx, fz] = at(2.4, across);
    const bed = new THREE.Mesh(
      new THREE.BoxGeometry(sideEntry ? 1.5 : 3.4, 0.22, sideEntry ? 3.4 : 1.5),
      soilMat,
    );
    bed.position.set(fx, 0.14, fz);
    bed.receiveShadow = true;
    group.add(bed);
    const petalMats = [
      standard(0xc4506a, { rough: 0.9 }),
      standard(0xd8a03a, { rough: 0.9 }),
      standard(0xe8e2d0, { rough: 0.9 }),
    ];
    for (let i = 0; i < 9; i++) {
      const flower = new THREE.Mesh(
        new THREE.IcosahedronGeometry(0.11, 0),
        petalMats[Math.floor(rand() * petalMats.length)]!,
      );
      flower.position.set(
        fx + (rand() - 0.5) * (sideEntry ? 1.1 : 3.0),
        0.3 + rand() * 0.12,
        fz + (rand() - 0.5) * (sideEntry ? 3.0 : 1.1),
      );
      group.add(flower);
    }
  }

  // --- the other end: a neglected property collects things -----------------
  if (upkeep < 0.4) {
    const across = (halfAcross - 2.2) * (rand() > 0.5 ? 1 : -1);
    const [sx, sz] = at(3.0, across);
    // A stack of forgotten timber, greying in the corner of the yard.
    const timberMat = standard(0x7d7266, { rough: 1 });
    for (let i = 0; i < 3; i++) {
      const plank = new THREE.Mesh(new THREE.BoxGeometry(2.1, 0.14, 0.4), timberMat);
      plank.position.set(sx + (rand() - 0.5) * 0.2, 0.14 + i * 0.15, sz + (rand() - 0.5) * 0.3);
      plank.rotation.y = rand() * 0.5 - 0.25;
      plank.castShadow = true;
      group.add(plank);
    }
  }
}

/**
 * The forecourt of a shop, bank or depot office.
 *
 * A commercial street should not look like a residential one with different
 * paint. What separates them in life is the ground: a wide paved apron instead
 * of grass, bollards, planters, a bike rack, and a board out on the pavement.
 */
function addCommercialFrontage(
  group: THREE.Group,
  location: WorldLocation,
  sideEntry: boolean,
): void {
  let seed = 0;
  for (const ch of location.id) seed = (seed * 37 + ch.charCodeAt(0)) & 0x7fffffff;
  const rand = () => {
    seed = (seed * 1103515245 + 12345) & 0x7fffffff;
    return seed / 0x7fffffff;
  };

  const outward = sideEntry ? Math.sign(location.entryX) : Math.sign(location.entryZ);
  const halfOut = (sideEntry ? location.width : location.depth) / 2;
  const across = sideEntry ? location.depth : location.width;
  const at = (out: number, side: number): [number, number] =>
    sideEntry ? [outward * (halfOut + out), side] : [side, outward * (halfOut + out)];

  // Paved apron running the full frontage.
  {
    const depth = 9;
    const [ax, az] = at(depth / 2, 0);
    const apron = new THREE.Mesh(
      new THREE.PlaneGeometry(sideEntry ? depth : across + 6, sideEntry ? across + 6 : depth),
      pavementMaterial(),
    );
    apron.rotation.x = -Math.PI / 2;
    apron.position.set(ax, 0.068, az);
    apron.receiveShadow = true;
    group.add(apron);
  }

  // Bollards along the kerb edge of the apron.
  const bollardMat = standard(0x3a4048, { rough: 0.6, metal: 0.35 });
  const capMat = standard(0xd8c46a, { rough: 0.7 });
  const count = Math.max(3, Math.round(across / 5));
  for (let i = 0; i < count; i++) {
    const side = -across / 2 + (across / (count - 1)) * i;
    if (Math.abs(side) < 2.4) continue; // leave the doorway clear
    const [bx, bz] = at(8.2, side);
    const post = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.14, 0.92, 8), bollardMat);
    post.position.set(bx, 0.46, bz);
    post.castShadow = true;
    group.add(post);
    const cap = new THREE.Mesh(new THREE.CylinderGeometry(0.15, 0.15, 0.1, 8), capMat);
    cap.position.set(bx, 0.95, bz);
    group.add(cap);
  }

  // Planters either side of the entrance.
  for (const side of [-1, 1]) {
    const [px, pz] = at(3.2, side * Math.min(across / 2 - 1.6, 4.4));
    const tub = new THREE.Mesh(
      new THREE.BoxGeometry(1.5, 0.7, 1.5),
      standard(0x8a8078, { rough: 0.95 }),
    );
    tub.position.set(px, 0.35, pz);
    tub.castShadow = true;
    tub.receiveShadow = true;
    group.add(tub);
    const foliageMat = standard(0x3d6f31, { rough: 1 });
    for (let i = 0; i < 3; i++) {
      const lobe = new THREE.Mesh(new THREE.IcosahedronGeometry(0.42 + rand() * 0.2, 0), foliageMat);
      lobe.position.set(px + (rand() - 0.5) * 0.7, 0.9 + rand() * 0.25, pz + (rand() - 0.5) * 0.7);
      lobe.castShadow = true;
      group.add(lobe);
    }
  }

  // A bike rack: two hoops, the cheapest possible read on "people come here".
  {
    const side = (across / 2 - 3) * (rand() > 0.5 ? 1 : -1);
    const rackMat = standard(0x9aa1a8, { rough: 0.5, metal: 0.6 });
    for (const offset of [-0.55, 0.55]) {
      const [hx, hz] = at(5.6, side + offset);
      const hoop = new THREE.Mesh(new THREE.TorusGeometry(0.42, 0.045, 6, 12, Math.PI), rackMat);
      hoop.position.set(hx, 0.06, hz);
      hoop.rotation.y = sideEntry ? Math.PI / 2 : 0;
      hoop.castShadow = true;
      group.add(hoop);
    }
  }
}

/**
 * The depot's working yard: pallets, crates, a container and bay markings.
 *
 * The depot is where the player takes shifts, so it has to look like somewhere
 * work happens rather than a shop with a different sign.
 */
function addDepotYard(
  group: THREE.Group,
  location: WorldLocation,
  sideEntry: boolean,
): void {
  let seed = 991;
  const rand = () => {
    seed = (seed * 1103515245 + 12345) & 0x7fffffff;
    return seed / 0x7fffffff;
  };
  const outward = sideEntry ? Math.sign(location.entryX) : Math.sign(location.entryZ);
  const halfOut = (sideEntry ? location.width : location.depth) / 2;
  const across = sideEntry ? location.depth : location.width;
  const at = (out: number, side: number): [number, number] =>
    sideEntry ? [outward * (halfOut + out), side] : [side, outward * (halfOut + out)];

  {
    const depth = 13;
    const [ax, az] = at(depth / 2, 0);
    const apron = new THREE.Mesh(
      new THREE.PlaneGeometry(sideEntry ? depth : across + 8, sideEntry ? across + 8 : depth),
      pavementMaterial(),
    );
    apron.rotation.x = -Math.PI / 2;
    apron.position.set(ax, 0.068, az);
    apron.receiveShadow = true;
    group.add(apron);
  }

  // Painted loading bays.
  const paintMat = new THREE.MeshStandardMaterial({
    color: 0xe8d34a, roughness: 0.9, transparent: true, opacity: 0.75,
  });
  for (let i = -1; i <= 1; i++) {
    const [bx, bz] = at(6.5, i * 4.6);
    const stripe = new THREE.Mesh(
      new THREE.PlaneGeometry(sideEntry ? 9 : 0.22, sideEntry ? 0.22 : 9),
      paintMat,
    );
    stripe.rotation.x = -Math.PI / 2;
    stripe.position.set(bx, 0.075, bz);
    group.add(stripe);
  }

  // Crates stacked against the frontage.
  const crateMats = [
    standard(0x8a6f4a, { rough: 1 }),
    standard(0x6f5a3c, { rough: 1 }),
    standard(0x4a5560, { rough: 0.9 }),
  ];
  for (let i = 0; i < 5; i++) {
    const side = (rand() - 0.5) * (across - 5);
    const [cx, cz] = at(1.8 + rand() * 1.6, side);
    const stack = 1 + Math.floor(rand() * 3);
    for (let j = 0; j < stack; j++) {
      const crate = new THREE.Mesh(
        new THREE.BoxGeometry(1.1, 0.85, 1.1),
        crateMats[Math.floor(rand() * crateMats.length)]!,
      );
      crate.position.set(cx + (rand() - 0.5) * 0.2, 0.43 + j * 0.86, cz + (rand() - 0.5) * 0.2);
      crate.rotation.y = (rand() - 0.5) * 0.5;
      crate.castShadow = true;
      crate.receiveShadow = true;
      group.add(crate);
    }
  }

  // A shipping container along one end of the yard.
  {
    const side = (across / 2 - 3.5) * (rand() > 0.5 ? 1 : -1);
    const [kx, kz] = at(9.5, side);
    const container = new THREE.Mesh(
      new THREE.BoxGeometry(sideEntry ? 2.6 : 7.2, 2.7, sideEntry ? 7.2 : 2.6),
      standard(0x9a4a3a, { rough: 0.75, metal: 0.2 }),
    );
    container.position.set(kx, 1.35, kz);
    container.castShadow = true;
    container.receiveShadow = true;
    group.add(container);
  }
}

function buildMarker(height: number): THREE.Group {
  const marker = new THREE.Group();
  const beacon = new THREE.Mesh(
    new THREE.ConeGeometry(1.1, 2.4, 6),
    new THREE.MeshBasicMaterial({ color: 0xffd23f, fog: false }),
  );
  beacon.rotation.x = Math.PI;
  beacon.position.y = height;
  marker.add(beacon);

  // A soft halo so the beacon reads at distance without being a laser.
  const halo = new THREE.Mesh(
    new THREE.SphereGeometry(1.6, 12, 8),
    new THREE.MeshBasicMaterial({
      color: 0xffd23f, transparent: true, opacity: 0.16, depthWrite: false, fog: false,
    }),
  );
  halo.position.y = height;
  marker.add(halo);

  marker.visible = false;
  return marker;
}

export function shade(colour: number, amount: number): number {
  const c = new THREE.Color(colour);
  const hsl = { h: 0, s: 0, l: 0 };
  c.getHSL(hsl);
  c.setHSL(hsl.h, hsl.s, Math.max(0, Math.min(1, hsl.l + amount)));
  return c.getHex();
}
